// RegisterForm.jsx (React web)
// - Robust membership counter (auto-init if missing)
// - Saves: name, email, accountType (+label), membershipNumber, joinedDate, lastLoggedIn, searchName
// - Default settings: profilePrivate=false, mfaEnabled=false
// - Captures invitationCode & termsAcceptedAt
// - Extra email uniqueness check using Auth

import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { auth, db } from '@root/firebase';
import { useAuth } from '@context/AuthContext';
import Modal from '@/components/Modal';

import {
  doc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  runTransaction,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { fetchSignInMethodsForEmail } from 'firebase/auth';

import {
  passwordLength,
  passwordLetterBoolean,
  passwordNumBoolean,
  passwordSpecialCharBoolean,
  validateEmailFormat,
} from '../utils/Index';

const RegisterForm = ({ onSwitchForm }) => {
  const { register } = useAuth();
  const logo =
    'https://firebasestorage.googleapis.com/v0/b/smartdog-bbe9f.firebasestorage.app/o/images%2Flogo-full.jpg?alt=media&token=685d3267-d81d-4256-b88c-f776c0e08363';

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isEmailAvailable, setIsEmailAvailable] = useState(true);
  const [emailErrorMessage, setEmailErrorMessage] = useState('');

  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [isLengthValid, setIsLengthValid] = useState(false);
  const [hasBigLetter, setHasBigLetter] = useState(false);
  const [hasNumber, setHasNumber] = useState(false);
  const [hasSpecialChar, setHasSpecialChar] = useState(false);

  const [testRegistration, setTestRegistration] = useState(false);

  //const [invitationCode, setInvitationCode] = useState('');
  //const [isInvitationValid, setIsInvitationValid] = useState(true);

  const [reenterMatch, setReenterMatch] = useState(false);
  const [showReenterTooltip, setShowReenterTooltip] = useState(false);
  const [showPasswordTooltip, setShowPasswordTooltip] = useState(false);
  const [showInvalidEmailTooltip, setShowInvalidEmailTooltip] = useState(false);
  const [showEmailUnavailableTooltip, setShowEmailUnavailableTooltip] =
    useState(false);

  const [registrationType, setRegistrationType] = useState('User'); // "User" | "Participant"
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    // debug aid you already had
    console.log('Email Error Message:', emailErrorMessage);
  }, [emailErrorMessage]);

  //function validateInvitationCode() {
  //  setIsInvitationValid(invitationCode.trim() === 'smartdog2025');
  //}

  /* ---------------- Email change: validate + availability (Auth + Firestore) ---------------- */
  const handleEmailChange = async (value) => {
    const em = value.trim().toLowerCase();
    setEmail(em);

    const valid = validateEmailFormat(em);
    setIsEmailValid(valid);

    if (!valid) {
      setShowInvalidEmailTooltip(true);
      setShowEmailUnavailableTooltip(false);
      setIsEmailAvailable(true);
      setEmailErrorMessage('Invalid email');
      return;
    }

    // Check Auth providers to catch already registered emails
    try {
      const providers = await fetchSignInMethodsForEmail(auth, em);
      if (providers.length > 0) {
        setIsEmailAvailable(false);
        setEmailErrorMessage('Unavailable');
        setShowEmailUnavailableTooltip(true);
        return;
      }
    } catch {
      // ignore, we still do Firestore check below
    }

    // Check Firestore users (in case a ghost doc exists)
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', em));
    const qs = await getDocs(q);
    if (!qs.empty) {
      setIsEmailAvailable(false);
      setEmailErrorMessage('Unavailable');
      setShowEmailUnavailableTooltip(true);
    } else {
      setIsEmailAvailable(true);
      setEmailErrorMessage('');
      setShowInvalidEmailTooltip(false);
      setShowEmailUnavailableTooltip(false);
    }
  };

  /* ---------------- Password validation ---------------- */
  function validatePassword() {
    const lengthValid = passwordLength(password);
    const bigLetterValid = passwordLetterBoolean(password);
    const numberValid = passwordNumBoolean(password);
    const specialCharValid = passwordSpecialCharBoolean(password);

    setIsLengthValid(lengthValid);
    setHasBigLetter(bigLetterValid);
    setHasNumber(numberValid);
    setHasSpecialChar(specialCharValid);

    setShowPasswordTooltip(
      !(lengthValid && bigLetterValid && numberValid && specialCharValid)
    );
  }

  function validateReenterPassword() {
    const match = password === password2;
    setReenterMatch(match);
    setShowReenterTooltip(!match);
  }

  const validationCriteria = [
    { label: '8 chars', isValid: isLengthValid },
    { label: '1 big letter', isValid: hasBigLetter },
    { label: '1 number', isValid: hasNumber },
    { label: '1 special char', isValid: hasSpecialChar },
  ];

  /* ---------------- Robust membership number (auto-init counter if missing) ---------------- */
  async function getNextMembershipNumber() {
    const counterRef = doc(db, 'counters', 'membership');
    return await runTransaction(db, async (t) => {
      const snap = await t.get(counterRef);
      let last = 100000; // starting floor (customize if needed)
      if (snap.exists()) {
        const val = Number(snap.data()?.lastNumber);
        last = Number.isFinite(val) ? val : last;
      }
      const next = last + 1;

      if (snap.exists()) {
        t.update(counterRef, { lastNumber: next });
      } else {
        t.set(counterRef, { lastNumber: next });
      }
      return next;
    });
  }

  /* ---------------- Register ---------------- */
  async function handleRegistration() {
    const _name = name.trim();
    const _email = email.trim().toLowerCase();

    if (!testRegistration) {
      if (!_name) return toast.error('Please enter your name.');
      if (!termsAccepted)
        return toast.error('Please accept the terms and conditions.');
      if (!registrationType)
        return toast.error('Please select a registration type.');

      // Ensure latest invite validity on submit
      //if (invitationCode && invitationCode.trim().length > 0)
      //validateInvitationCode();
      //if (!isInvitationValid) return toast.error('Invalid invitation code.');

      // Password checks
      if (!isLengthValid || !hasBigLetter || !hasNumber || !hasSpecialChar) {
        return toast.error('Password does not meet requirements.');
      }
      if (password !== password2) return toast.error('Passwords do not match.');

      // Extra email uniqueness guard with Auth
      try {
        const providers = await fetchSignInMethodsForEmail(auth, _email);
        if (providers.length > 0) {
          return toast.error('This email is already registered.');
        }
      } catch {
        // ignore
      }

      const defaultAvatarUrl =
        'https://firebasestorage.googleapis.com/v0/b/smartdog-bbe9f.firebasestorage.app/o/images%2Fuser.png?alt=media&token=daf30f34-5f3f-4f3d-bbdc-de5b0441365b';

      try {
        // 1) Create Auth account via your context
        const userCredential = await register(_email, password);
        const uid = userCredential.user.uid;

        // 2) Membership number (fallback if transaction fails)
        let membershipNumber;
        try {
          membershipNumber = await getNextMembershipNumber();
        } catch (e) {
          console.warn('Membership counter failed, using fallback.', e);
          membershipNumber = Number(String(Date.now()).slice(-6));
        }

        // 3) Map role + label
        const accountType = registrationType; // "User" | "Participant"
        const accountTypeLabel =
          accountType === 'User'
            ? 'Dog Owner'
            : accountType === 'Participant'
            ? 'Marketplace Participant'
            : '';

        // 4) Build user doc (aligns with mobile)
        const userDoc = {
          uid,
          email: _email,
          name: _name,
          searchName: _name.toLowerCase(),
          avatar: defaultAvatarUrl,
          isAdmin: false,
          accountType, // ✅ present
          accountTypeLabel, // ✅ present
          membershipNumber, // ✅ present
          joinedDate: serverTimestamp(), // use server clock
          lastLoggedIn: serverTimestamp(), // capture current login
          //invitationCodeUsed: invitationCode || '',
          termsAcceptedAt: serverTimestamp(),
        };

        // 5) Write user + settings
        await setDoc(doc(db, 'users', uid), userDoc);
        await setDoc(
          doc(db, 'users', uid, 'settings', 'main'),
          { profilePrivate: false, mfaEnabled: false },
          { merge: true }
        );

        console.log('[RegisterWeb] wrote userDoc:', userDoc);
        toast.success('You have successfully registered');
        setTimeout(() => onSwitchForm('login'), 1500);
      } catch (err) {
        console.error('register failed:', err);
        toast.error(err?.message || 'Registration failed');
      }
    } else {
      toast.success('You have successfully registered');
      setTimeout(() => onSwitchForm('login'), 1500);
    }
  }

  /* ---------------- JSX ---------------- */
  return (
    <div className='authentication-bg min-vh-100'>
      <div className='bg-overlay bg-light'></div>
      <div className='container'>
        <div className='d-flex flex-column min-vh-100 px-3 pt-4'>
          <div className='row justify-content-center my-auto'>
            <div className='col-md-8 col-lg-6 col-xl-5'>
              <div className='card'>
                <div className='card-body p-4'>
                  <div className='text-center mt-2'>
                    <img src={logo} alt='' width='300' className='mb-3' />
                    <h5>Create an Account</h5>
                    <p className='text-muted'>Register to SmartDog.</p>
                  </div>

                  <div className='p-2 mt-4'>
                    {/* Email */}
                    <div className='form-floating mb-3 position-relative'>
                      <input
                        type='email'
                        className='form-control'
                        placeholder='Email'
                        value={email}
                        onChange={(e) => handleEmailChange(e.target.value)}
                      />
                      <label className='form-label'>Email</label>

                      {(showInvalidEmailTooltip && !isEmailValid) ||
                      (showEmailUnavailableTooltip && !isEmailAvailable) ? (
                        <div
                          className='invalid-overlay'
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: '20%',
                            backgroundColor: !isEmailValid
                              ? '#FDF3D7'
                              : '#EF6666',
                            boxShadow: '0px 4px 6px rgba(0,0,0,0.1)',
                            padding: '5px 15px',
                            zIndex: 10,
                            width: 'auto',
                            borderRadius: '9999px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <ul className='list-unstyled mb-0'>
                            {showInvalidEmailTooltip && !isEmailValid && (
                              <li
                                className='requirements'
                                style={{ color: '#927422' }}
                              >
                                <i className='fas fa-times text-warning me-3'></i>
                                Invalid email
                              </li>
                            )}
                            {showEmailUnavailableTooltip &&
                              !isEmailAvailable && (
                                <li
                                  className='requirements'
                                  style={{
                                    color: !isEmailValid ? '#927422' : 'white',
                                  }}
                                >
                                  <i className='fas fa-times text-white me-3'></i>
                                  Unavailable
                                </li>
                              )}
                          </ul>
                        </div>
                      ) : null}
                    </div>

                    {/* Name */}
                    <div className='form-floating mb-3 position-relative'>
                      <input
                        type='text'
                        className='form-control'
                        placeholder='Name'
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                      <label className='form-label'>Name</label>
                    </div>

                    {/* Password */}
                    <div className='form-floating mb-3 position-relative'>
                      <input
                        type='password'
                        className='form-control'
                        placeholder='Password'
                        value={password}
                        onKeyUp={validatePassword}
                        onChange={(e) => setPassword(e.target.value)}
                        onFocus={() => setShowPasswordTooltip(true)}
                      />
                      <label className='form-label'>Password</label>

                      {showPasswordTooltip && (
                        <div
                          className='invalid-overlay'
                          data-mdb-color='warning'
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: '20%',
                            backgroundColor: '#FDF3D7',
                            boxShadow: '0px 4px 6px rgba(0,0,0,0.1)',
                            padding: '10px',
                            zIndex: 10,
                            width: '50%',
                            borderRadius: '4px',
                          }}
                        >
                          <ul className='list-unstyled mb-0'>
                            {validationCriteria.map((criterion, i) => (
                              <li
                                key={i}
                                className='requirements special-char'
                                style={{ color: '#927422' }}
                              >
                                {criterion.isValid ? (
                                  <i className='fas fa-check text-success me-2'></i>
                                ) : (
                                  <i className='fas fa-times text-warning me-3'></i>
                                )}
                                {criterion.label}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Re-enter Password */}
                    <div className='form-floating mb-3 position-relative'>
                      <input
                        type='password'
                        className='form-control'
                        placeholder='Re-enter Password'
                        value={password2}
                        onKeyUp={validateReenterPassword}
                        onChange={(e) => setPassword2(e.target.value)}
                        onFocus={() => setShowReenterTooltip(true)}
                      />
                      <label className='form-label'>Re-enter</label>

                      {showReenterTooltip && (
                        <div
                          className='invalid-overlay'
                          data-mdb-color='warning'
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: '20%',
                            backgroundColor: '#FDF3D7',
                            boxShadow: '0px 4px 6px rgba(0,0,0,0.1)',
                            padding: '5px 15px',
                            zIndex: 10,
                            width: 'auto',
                            borderRadius: '9999px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <ul className='list-unstyled mb-0'>
                            <li
                              className='requirements match'
                              style={{ color: '#927422' }}
                            >
                              {reenterMatch ? (
                                <i className='fas fa-check text-success me-2'></i>
                              ) : (
                                <i className='fas fa-times text-warning me-3'></i>
                              )}
                              must match
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Account Type */}
                    <div className='form-floating mb-3 position-relative'>
                      <select
                        className='form-select'
                        value={registrationType}
                        onChange={(e) => setRegistrationType(e.target.value)}
                      >
                        <option value='User'>Dog Owner</option>
                        <option value='Participant'>
                          Marketplace Participant
                        </option>
                      </select>
                      <label>Registration Type</label>
                    </div>

                    {/* Invitation Code 
                    <div className='form-floating mb-3 position-relative'>
                      <input
                        type='text'
                        className='form-control'
                        placeholder='Invitation Code'
                        value={invitationCode}
                        onChange={(e) => setInvitationCode(e.target.value)}
                        onBlur={validateInvitationCode}
                      />
                      <label className='form-label'>Invitation Code</label>
                    </div>
                    */}

                    {/* T&Cs */}
                    <div className='mt-3'>
                      <div className='form-check py-1'>
                        <input
                          type='checkbox'
                          className='form-check-input'
                          id='auth-terms-condition-check'
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                        />
                        <label
                          className='form-check-label text-dark'
                          htmlFor='auth-terms-condition-check'
                        >
                          I accept the{' '}
                          <a
                            href='#'
                            className='text-primary'
                            data-bs-toggle='modal'
                            data-bs-target='#terms'
                          >
                            Terms and Conditions
                          </a>
                        </label>
                      </div>
                    </div>

                    <div className='mt-3'>
                      <button
                        onClick={handleRegistration}
                        className='btn btn-primary w-100'
                        disabled={
                          !isEmailValid ||
                          !isEmailAvailable ||
                          !isLengthValid ||
                          !hasBigLetter ||
                          !hasNumber ||
                          !hasSpecialChar ||
                          password !== password2 ||
                          !termsAccepted ||
                          !name
                        }
                      >
                        Register
                      </button>
                    </div>

                    <div className='mt-4 text-center'>
                      <p>
                        Already have an account?{' '}
                        <a
                          href='#'
                          onClick={() => onSwitchForm('login')}
                          className='fw-medium text-primary'
                        >
                          Login
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Modal
            id='terms'
            title='Smartdog Terms and Conditions'
            showFooter={false}
            size='lg'
          ></Modal>

          <div className='row'>
            <div className='col-lg-12'>
              <div className='text-center p-4'>
                <p>
                  Copyright © Smart Dog - All Rights Reserved -{' '}
                  <a
                    href='https://www.sis-smartdog.com'
                    target='_blank'
                    rel='noreferrer'
                  >
                    www.sis-smartdog.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;
